import { inject, Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { catchError, tap } from 'rxjs';
import { AppActions } from '../../../state/app.actions';
import { MeetCallService } from '../services/meetcall.service';
import { MeetCallActions } from './meetcall.actions';
import {
  INITIAL_MEETCALL_STATE,
  MEETCALL_STATE_TOKEN,
  MeetCallStateModel,
} from './meetcall.models';
import { LiveRecruterScoresData } from './meetcall.types';

@State<MeetCallStateModel>({
  name: MEETCALL_STATE_TOKEN,
  defaults: INITIAL_MEETCALL_STATE,
})
@Injectable()
export class MeetCallState {
  private suggestionTimer?: ReturnType<typeof setTimeout>;
  private meetCallService = inject(MeetCallService);
  private clearSuggestionValue(ctx: StateContext<MeetCallStateModel>) {
    if (this.suggestionTimer) {
      clearTimeout(this.suggestionTimer);
      this.suggestionTimer = undefined;
    }
    ctx.patchState({
      suggestion: null,
    });
  }

  @Action(MeetCallActions.setRoomTranscription)
  setRoomTranscriptions(
    ctx: StateContext<MeetCallStateModel>,
    action: InstanceType<typeof MeetCallActions.setRoomTranscription>,
  ) {
    //console.log('📥 Setting room transcriptions in state:', action.roomTranscription);
    ctx.patchState({
      roomTranscription: action.roomTranscription,
    });
  }

  @Action(MeetCallActions.joinMeetCall)
  joinMeetCall(
    ctx: StateContext<MeetCallStateModel>,
    action: InstanceType<typeof MeetCallActions.joinMeetCall>,
  ) {
    const scores = ctx.getState().candidatScores ?? [];
    const isExternal = action.data.memberMeta.role === 'CANDIDATE';
    const liveScoresByMember = (action.data.stats?.['LIVE_SCORES'] ?? {}) as unknown as {
      [memberId: string]: { [key: string]: number };
    };
    const eventInitScores = isExternal
      ? []
      : Object.entries(liveScoresByMember).map(([memberId, memberScores]): LiveRecruterScoresData => {
          return {
            id: '',
            memberId,
            scores: memberScores,
          };
        });
    ctx.patchState({
      joinData: action.data,
      candidatScores: [...scores, ...eventInitScores],
      comments: (action.data.comments ?? []).sort((a: any, b: any) => b.updatedAt - a.updatedAt),
    });
  }

  @Action(MeetCallActions.setCandidatScores)
  setCandidatScores(
    ctx: StateContext<MeetCallStateModel>,
    action: InstanceType<typeof MeetCallActions.setCandidatScores>,
  ) {
    let candidatScores = ctx.getState().candidatScores ?? [];
    if (!candidatScores.find((row) => row.memberId === action.data.memberId)) {
      candidatScores = [...candidatScores, action.data];
      ctx.patchState({
        candidatScores: candidatScores,
      });
      return;
    }
    candidatScores = candidatScores.map((row) => {
      if (row.memberId === action.data.memberId) {
        return action.data;
      }
      return row;
    });
    ctx.patchState({
      candidatScores: candidatScores,
    });
  }

  @Action(MeetCallActions.saveSuggestion)
  saveSuggestion(
    ctx: StateContext<MeetCallStateModel>,
    action: InstanceType<typeof MeetCallActions.saveSuggestion>,
  ) {
    ctx.patchState({
      suggestion: action.data,
    });
    if (this.suggestionTimer) {
      clearTimeout(this.suggestionTimer);
    }
    this.suggestionTimer = setTimeout(() => {
      this.clearSuggestionValue(ctx);
    }, 60000);
  }

  @Action(MeetCallActions.clearSuggestion)
  clearSuggestion(ctx: StateContext<MeetCallStateModel>) {
    this.clearSuggestionValue(ctx);
  }

  @Action(MeetCallActions.saveComment)
  saveComment(
    ctx: StateContext<MeetCallStateModel>,
    action: InstanceType<typeof MeetCallActions.saveComment>,
  ) {
    const state = ctx.getState();
    return this.meetCallService
      .saveComment(action.data, state.linkData, state.joinData?.companyId ?? '')
      .pipe(
        tap((result) => {
          ctx.patchState({
            comments: [result, ...ctx.getState().comments],
          });
        }),
        catchError((error) => {
          ctx.dispatch(new AppActions.toastError(error));
          return error;
        }),
      );
  }

  @Action(MeetCallActions.setLinkData)
  setLinkData(
    ctx: StateContext<MeetCallStateModel>,
    action: InstanceType<typeof MeetCallActions.setLinkData>,
  ) {
    ctx.patchState({
      linkData: action.data,
    });
  }
}
